import { downloadBlob } from 'src/cashflows/modelling/export/downloadBlob';
import {
  ConversationDebug,
  ConversationMessage,
} from 'src/secret-menu/conversation-debugger/types';

type ConversationExport = {
  exportVersion: 1;
  exportedAt: string;
  tenantName: string;
  conversation: Omit<ConversationDebug, 'messages'>;
  notes: string[];
  messages: ConversationMessage[];
};

const MESSAGE_SHAPE_NOTE =
  'Messages are as stored, in order, except that download links Siesta presented are re-signed when the debugger loads the conversation and stop working an hour later. An assistant message that calls tools is stored with a null role; its tool_calls and the thinking for that round (reasoning_content) sit on the same message, and each result follows as a message with the role "tool" and a matching tool_call_id.';

const TOOL_DEFINITIONS_NOTE =
  'The tool definitions sent to the model are never stored, so only the calls and their results appear here.';

const CHAT_SYSTEM_PROMPT_NOTE =
  'The first system message is the Siesta system prompt as it was written on the first turn of the conversation. Siesta also adds extra system notes to every turn (household focus, uploads, the requested skill, the memory workspace, meeting actions and others). Most of these are removed before the conversation is saved, so they are not in this file.';

const DOCUMENT_EDIT_SYSTEM_PROMPT_NOTE =
  'The document editor rebuilds its system prompt on every turn and removes system messages before saving, so its system prompt is usually missing from this file.';

function compactionNote(compactionCount: number): string {
  const times = compactionCount === 1 ? 'once' : `${compactionCount} times`;
  return `This conversation was compacted ${times}. Older tool calls and tool results were replaced by a summary, held in the system messages that start with [AUTO-COMPACTED CONVERSATION], and thinking was removed from all but the most recent messages. The original messages are not stored.`;
}

export function buildConversationExport(
  conversation: ConversationDebug,
  tenantName: string,
  exportedAt: Date,
): ConversationExport {
  const { messages, ...details } = conversation;
  const notes = [
    MESSAGE_SHAPE_NOTE,
    conversation.source === 'DOCUMENT_EDIT'
      ? DOCUMENT_EDIT_SYSTEM_PROMPT_NOTE
      : CHAT_SYSTEM_PROMPT_NOTE,
    TOOL_DEFINITIONS_NOTE,
  ];
  if (conversation.compactionCount > 0) {
    notes.push(compactionNote(conversation.compactionCount));
  }

  return {
    exportVersion: 1,
    exportedAt: exportedAt.toISOString(),
    tenantName,
    conversation: details,
    notes,
    messages,
  };
}

export function downloadConversationExport(
  conversation: ConversationDebug,
  tenantName: string,
): void {
  const exported = buildConversationExport(conversation, tenantName, new Date());
  const blob = new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' });
  downloadBlob(blob, `conversation-${conversation.id}.json`);
}
